import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { explain } from '@/shared/api/problem'
import { ErpTable, Td, Th } from '@/components/erp/primitives'
import { EmptyState, ErrorStrip, LoadingRows, Note } from '@/shared/ui/states'
import { fmtDateTime } from '@/shared/lib/format'
import { useElectionContext } from '@/shared/election/context'
import { NoElectionSelected } from '@/shared/election/ElectionSelector'
import { listVariances, resolveVariance, type TallyVariance } from './api'
import { resolutionLabel, seatLabel } from './vocab'

/**
 * Where the machine and the human disagreed.
 *
 * ## Resolving records a judgement; it does not change a number
 *
 * This surprises people, so the page says it. Accepting the machine's reading here does not
 * rewrite the return — that would be an edit with no version and no reason, which is the one
 * thing this module refuses. If the machine was right, the figure changes the only way
 * figures ever change: a correction on the return, with a reason.
 *
 * ## What the queue is actually for
 *
 * An unresolved disagreement blocks verification. So this list is the thing standing between
 * a return with an open question and a national total.
 */
export function VariancePage() {
  const { electionId } = useElectionContext()
  const [params, setParams] = useSearchParams()
  const showResolved = params.get('resolved') === '1'

  const [rows, setRows] = useState<TallyVariance[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!electionId) { setRows([]); return }
    try {
      setError(null)
      setRows(await listVariances({
        electionId,
        resolution: showResolved ? null : 'PENDING',
        size: 300,
      }))
    } catch (err) {
      setError(explain(err))
      setRows([])
    }
  }, [electionId, showResolved])

  useEffect(() => { void load() }, [load])

  if (!electionId) return <NoElectionSelected what="A variance queue" />

  const settle = async (id: string, resolution: string) => {
    setBusyId(id)
    setError(null)
    try {
      const note = resolution === 'AMENDED'
        ? window.prompt('What is the right value, and why? This is the only record of it.')
        : null
      if (resolution === 'AMENDED' && !note?.trim()) { setBusyId(null); return }
      await resolveVariance(id, resolution, note ?? undefined)
      await load()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-erp-base font-semibold">Variance</h2>
          <p className="text-erp-xs text-muted-foreground mt-0.5">
            Every field where a reviewer entered something different from what the machine
            read. The machine's value is kept exactly as it proposed it.
          </p>
        </div>
        <label className="inline-flex items-center gap-1.5 text-erp-xs text-muted-foreground">
          <input type="checkbox" checked={showResolved}
            onChange={(e) => setParams((p) => {
              if (e.target.checked) p.set('resolved', '1')
              else p.delete('resolved')
              return p
            }, { replace: true })} />
          Include the ones already settled
        </label>
      </div>

      <ErrorStrip message={error} className="rounded-md border" />

      {rows !== null && rows.length === 0 && !error && (
        <EmptyState
          title={showResolved ? 'No disagreements recorded' : 'Nothing unsettled'}
          hint="A disagreement appears here when somebody reviewing a form enters a number
                different from the one the machine read. None means either nobody has
                reviewed a form yet, or every reviewer agreed with the machine." />
      )}

      {(rows === null || rows.length > 0) && (
        <ErpTable head={(
          <>
            <Th>Field</Th>
            <Th>Race</Th>
            <Th>Place</Th>
            <Th className="text-right">Machine read</Th>
            <Th className="text-right">Entered</Th>
            <Th className="text-right">Difference</Th>
            <Th>Settled</Th>
            <Th />
          </>
        )}>
          {rows === null ? <LoadingRows colSpan={8} />
            : rows.map((v) => (
              <tr key={v.id}>
                <Td>{v.fieldLabel ?? v.fieldPath}</Td>
                <Td>{seatLabel(v.seatTypeCode)}</Td>
                <Td>{v.unitName ?? '—'}</Td>
                {/* Side by side, always — this pair IS the record. */}
                <Td className="text-right tabular-nums font-mono">{v.ocrValue ?? '—'}</Td>
                <Td className="text-right tabular-nums font-mono font-medium">
                  {v.enteredValue ?? '—'}
                </Td>
                <Td className={cn('text-right tabular-nums',
                  v.delta != null && v.delta !== 0 && 'text-amber-700 dark:text-amber-400')}>
                  {v.delta == null ? '—' : v.delta > 0 ? `+${v.delta}` : v.delta}
                </Td>
                <Td>
                  {v.resolutionCode === 'PENDING' ? (
                    <span className="text-erp-xs text-muted-foreground">unsettled</span>
                  ) : (
                    <span className="text-erp-xs">
                      {resolutionLabel(v.resolutionCode)}
                      {v.resolvedAt && (
                        <span className="block text-muted-foreground">
                          {fmtDateTime(v.resolvedAt)}
                        </span>
                      )}
                      {v.resolutionNote && (
                        <span className="block text-muted-foreground italic">
                          {v.resolutionNote}
                        </span>
                      )}
                    </span>
                  )}
                </Td>
                <Td>
                  {v.resolutionCode === 'PENDING' && (
                    <div className="flex items-center gap-1">
                      {busyId === v.id && <Loader2 className="h-3 w-3 animate-spin" />}
                      <button type="button" disabled={busyId !== null}
                        onClick={() => void settle(v.id, 'ACCEPTED_OCR')}
                        className="text-erp-xs text-chocolate hover:underline whitespace-nowrap">
                        machine
                      </button>
                      <span className="text-border">·</span>
                      <button type="button" disabled={busyId !== null}
                        onClick={() => void settle(v.id, 'ACCEPTED_ENTRY')}
                        className="text-erp-xs text-chocolate hover:underline whitespace-nowrap">
                        entry
                      </button>
                      <span className="text-border">·</span>
                      <button type="button" disabled={busyId !== null}
                        onClick={() => void settle(v.id, 'AMENDED')}
                        className="text-erp-xs text-chocolate hover:underline whitespace-nowrap">
                        neither
                      </button>
                    </div>
                  )}
                </Td>
              </tr>
            ))}
        </ErpTable>
      )}

      <Note tone="info">
        Settling a disagreement records <strong>who decided and what they decided</strong> — it
        does not change the return's figures. If the machine turns out to have been right, the
        number is changed the only way numbers ever change here: a correction on the return,
        with a recorded reason.
      </Note>
    </div>
  )
}
